import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { graphqls, MediaHelper, EditProfile } from '@blocs.common';
import styled from 'styled-components';
import { media } from '@minecraft.themes';
import { PrimaryButton, FlexDynamic, H3Text } from '@minecraft.atoms';
import DropdownItem from '@minecraft.dropdown-item';
import MeatballMenu from '@minecraft.meatball-menu';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { gqlProfileMedia, gqlMedia } from '@minecraft.graphql-types';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import CloudinaryVideoAudio from '@minecraft.cloudinary-video-audio';
import { useTranslation } from '@blocs.i18n';
import { WithIsPreview } from '../styles/ProfileView';
import { useProfileViewContext } from '../ProfileViewContext';
import { captureExceptionEvent } from '../../Sentry';
import { AddMediaDropdown } from './AddMediaDropdown';

const { StyledIcon } = EditProfile.CreditsMediaStyles;

const { UPDATE_PROFILE_MEDIA_MUTATION, UPDATE_PROFILE_MEDIA_LIST_MUTATION } = graphqls.profile;

const { useMediaText } = MediaHelper;

export const MediaContainer = styled.div<WithIsPreview>`
  background: ${(props): string => props.theme.colors.accent.greyBlue2};
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  padding: ${(props): string => (props.isPreview ? '0' : '0 1rem')};
  ${media.sm`
    justify-content: flex-start;
    margin-bottom: 1rem;
    padding: ${(props): string => (props.isPreview ? '0' : '1rem')};
  `}
`;

const MediaTitle = styled.label`
  font-size: 1.3rem;
  font-weight: bold;
`;

const MediaGrid = styled.div`
  display: flex;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  justify-content: center;
  ${media.sm`
    justify-content: initial;
  `}
`;

const PhotoMedia = styled.div`
  margin: 0 0.9375rem 1.25rem;
  max-width: 12rem;
  height: 15rem;
  box-sizing: content-box;
  border: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OtherMedia = styled.div<{ code: string }>`
  display: flex;
  flex-direction: column;
  margin: 0 0.9375rem 1.25rem;
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-wrap: inherit;
`;

const ProfilePhoto = styled(CloudinaryPhoto)`
  max-width: 100%;
  align-self: center;
`;

const StyledMeatballMenu = styled.div`
  position: absolute;
  top: 0.4rem;
  right: -1.3rem;
`;

const MediaName = styled.span`
  word-break: break-all;
  width: 16rem;
`;

type MeatballMenusProps = {
  direction: 'left' | 'right';
  'data-qa-id'?: string;
  hideMedia: () => Promise<void>;
  mediaType: MediaTypeCodeEnum;
  setMediaAsPrimary: () => Promise<void>;
  openEditPhotoModal?: () => void;
};
const MeatballMenus = ({
  direction,
  'data-qa-id': qaId,
  hideMedia,
  mediaType,
  setMediaAsPrimary,
  openEditPhotoModal,
}: MeatballMenusProps) => {
  const { t } = useTranslation();
  const getMediaText = useMediaText();
  const setPrimaryLabel = getMediaText(mediaType, 'setAsPrimary');

  return (
    <StyledMeatballMenu data-qa-id={`${qaId}-meatball`}>
      <MeatballMenu data-qa-id={`${qaId}-options`} direction={direction}>
        <DropdownItem
          data-qa-id={`${qaId}-set-as-primary-${mediaType.toLowerCase()}`}
          onClick={() => setMediaAsPrimary()}
        >
          {setPrimaryLabel}
        </DropdownItem>
        {mediaType === MediaTypeCodeEnum.Photo && (
          <DropdownItem data-qa-id={`${qaId}-edit-photo-thumbnail`} onClick={() => openEditPhotoModal()}>
            {t('common:profile.button.editPhotoThumbnail')}
          </DropdownItem>
        )}
        <DropdownItem data-qa-id={`${qaId}-hide-on-profile`} onClick={() => hideMedia()}>
          {t('common:profile.button.hideOnProfile')}
        </DropdownItem>
      </MeatballMenu>
    </StyledMeatballMenu>
  );
};

type MediaListProps = {
  'data-qa-id': string;
  openMediaViewer: (index: number, code: MediaTypeCodeEnum) => void;
  hideMedia: (mediaId: number) => Promise<void>;
  isPreview: boolean;
  mediaList?: gqlProfileMedia[];
  mediaType: MediaTypeCodeEnum;
  setMediaAsPrimary: (profileMediaId: number) => Promise<void>;
  openEditPhotoModal: (media: gqlMedia) => void;
};

const MediaList = ({
  'data-qa-id': qaId,
  openMediaViewer,
  hideMedia,
  isPreview,
  mediaList,
  mediaType,
  setMediaAsPrimary,
  openEditPhotoModal,
}: MediaListProps): JSX.Element => {
  return (
    <MediaGrid>
      {mediaList.map((mediaAsset, index) => {
        switch (mediaType) {
          case MediaTypeCodeEnum.Photo:
            return (
              <PhotoMedia key={mediaAsset.media.mediaId} data-testid={`media-on-profile-${mediaAsset?.media?.mediaId}`}>
                <ProfilePhoto
                  alt={mediaAsset.media.name}
                  data-qa-id={`${qaId}-${mediaType.toLowerCase()}`}
                  fileKey={mediaAsset.media.fileKey}
                  guid={mediaAsset.media.guid}
                  mediaStorageStatus={mediaAsset.media.mediaStorageStatus}
                  onClick={() => openMediaViewer(index, mediaAsset?.media?.mediaType?.code as MediaTypeCodeEnum)}
                  transformation={mediaAsset.media.transformation}
                  type="md"
                />
                {!isPreview && (
                  <MeatballMenus
                    data-qa-id={`${qaId}-${mediaType.toLowerCase()}`}
                    direction="right"
                    hideMedia={() => hideMedia(mediaAsset.media.mediaId)}
                    mediaType={mediaType}
                    setMediaAsPrimary={() => setMediaAsPrimary(mediaAsset.profileMediaId)}
                    openEditPhotoModal={() => openEditPhotoModal(mediaAsset.media)}
                  />
                )}
              </PhotoMedia>
            );
          case MediaTypeCodeEnum.Audio:
          case MediaTypeCodeEnum.Video:
            return (
              <OtherMedia
                code={mediaAsset.media.mediaType.code}
                key={mediaAsset.media.mediaId}
                data-qa-id={`${qaId}-${mediaType.toLowerCase()}-wrapper`}
                data-testid={`media-on-profile-${mediaAsset?.media?.mediaId}`}
              >
                <CloudinaryVideoAudio
                  controls={mediaType === MediaTypeCodeEnum.Audio}
                  data-qa-id={`${qaId}-${mediaType.toLowerCase()}`}
                  data-testid={`media-item-${mediaType.toLowerCase()}`}
                  fileKey={mediaAsset.media.fileKey}
                  guid={mediaAsset.media.guid}
                  isThumbnail
                  mediaStatus={mediaAsset.media.mediaStatus.code}
                  mediaStorageStatus={mediaAsset.media.mediaStorageStatus.code}
                  mediaType={mediaAsset.media.mediaType.code as MediaTypeCodeEnum}
                  thumbnail={mediaAsset.media.thumbnail}
                  thumbnailUrl={mediaAsset.media.thumbnailUrl}
                  title={mediaAsset.media.name}
                  type="sm"
                />
                {mediaAsset.media.mediaType.code === MediaTypeCodeEnum.Video && (
                  <StyledIcon
                    data-qa-id={`${qaId}-${mediaType.toLowerCase()}-play-icon`}
                    name="play-circle-filled"
                    onClick={() => openMediaViewer(index, mediaAsset.media.mediaType.code as MediaTypeCodeEnum)}
                  />
                )}
                {!isPreview && (
                  <MeatballMenus
                    data-qa-id={`${qaId}-${mediaType.toLowerCase()}`}
                    direction="right"
                    hideMedia={() => hideMedia(mediaAsset?.media?.mediaId)}
                    setMediaAsPrimary={() => setMediaAsPrimary(mediaAsset?.profileMediaId)}
                    mediaType={mediaType}
                  />
                )}
                <MediaName data-qa-id={`${qaId}-${mediaType.toLowerCase()}-name`}>{mediaAsset.media.name}</MediaName>
              </OtherMedia>
            );
          default:
            return null;
        }
      })}
    </MediaGrid>
  );
};

type MediaDisplayProps = {
  'data-qa-id': string;
  mediaList: gqlProfileMedia[];
  mediaType: MediaTypeCodeEnum;
  openMediaBin: () => void;
  openMediaUploader: () => void;
  openMediaViewer: (index: number, code: MediaTypeCodeEnum) => void;
  openPhotoEditor: (media: gqlMedia) => void;
  openRearrangeModal: () => void;
  removeProfileMedia: (params: { mediaId: number }) => Promise<void>;
  uploadNewAllowed: boolean;
};

export const MediaDisplay = ({
  'data-qa-id': qaId,
  mediaList,
  mediaType,
  openMediaBin,
  openMediaUploader,
  openMediaViewer,
  openPhotoEditor,
  openRearrangeModal,
  removeProfileMedia,
  uploadNewAllowed,
}: MediaDisplayProps): JSX.Element => {
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();
  const { fetcher, isPreview, isRearrangeMedia } = useProfileViewContext();
  const { refetch } = fetcher;

  const getMediaText = useMediaText();
  const [updateProfileMediaMutation] = useMutation(UPDATE_PROFILE_MEDIA_MUTATION);
  const [updateProfileMediaListMutation] = useMutation(UPDATE_PROFILE_MEDIA_LIST_MUTATION);

  const setMediaAsPrimary = useCallback(
    async (mediaId: number) => {
      switch (mediaType) {
        case MediaTypeCodeEnum.Photo: {
          const primaryIndex = mediaList.findIndex((item) => item.isPrimary === true);
          let profileMediaList = MediaHelper.rearrangeMedias(
            mediaList.map(({ profileMediaId, isPrimary, order }) => ({
              profileMediaId,
              isHidden: false,
              isPrimary,
              order,
            })),
            primaryIndex,
            true,
            true
          );
          const mediaIndex = profileMediaList.findIndex((item) => item.profileMediaId === mediaId);

          profileMediaList = MediaHelper.rearrangeMedias(profileMediaList, mediaIndex, true, true);
          profileMediaList = profileMediaList.map((item, index) => {
            return {
              ...item,
              order: (index as number) + 1,
              isPrimary: index === 0,
            };
          });
          try {
            const res = await updateProfileMediaListMutation({ variables: { profileMediaList } });

            if (res?.errors) {
              throw res?.errors;
            }
            await refetch({ refetchProfile: true, refetchMedia: true });
          } catch (e) {
            captureExceptionEvent(e);
            showMessage({ color: 'danger', message: t('common:profile.mediaBank.setPrimaryFailed') });
          }
          break;
        }
        case MediaTypeCodeEnum.Video:
        case MediaTypeCodeEnum.Audio:
        default: {
          try {
            const res = await updateProfileMediaMutation({
              variables: { profileMedia: { profileMediaId: mediaId, isPrimary: true } },
            });
            if (res?.errors) {
              throw res?.errors;
            }
            await refetch({ refetchProfile: true, refetchMedia: true });
          } catch (e) {
            captureExceptionEvent(e);
            showMessage({ color: 'danger', message: t('common:profile.mediaBank.setPrimaryFailed') });
          }
          break;
        }
      }
    },
    [mediaList, mediaType, refetch, showMessage, t, updateProfileMediaListMutation, updateProfileMediaMutation]
  );

  const mediaTypeKey = mediaType?.toLowerCase();
  const headingText = getMediaText(mediaType, 'pluralTitle');
  const dropdownToggleLabel = getMediaText(mediaType, 'addClip');
  const rearrangeButtonText = t('common:mediaType.rearrange', { mediaType: headingText });

  // if there is no media to display and it's in preview mode, then
  // there is nothing to display
  if (isPreview && !mediaList?.length) {
    return <div data-testid={`profile-media-section-${mediaTypeKey}`} />;
  }

  // if there is media to display, then display it
  // there is some logic inside this block to render the preview/non-preview UI differences
  if (mediaList?.length) {
    return (
      <div data-testid={`profile-media-section-${mediaTypeKey}`}>
        <FlexDynamic
          direction="column"
          w="full"
          wrap="nowrap"
          gap="4"
          alignItems="flex-start"
          mb="4"
          smProps={{
            className: 'cn_atom_pl-4',
            alignItems: 'center',
            direction: 'row',
            justifyContent: 'space-between',
          }}
        >
          <H3Text fontWeight="semibold" data-qa-id={`${qaId}-${mediaType.toLowerCase()}-heading`}>
            {headingText}
          </H3Text>
          {!isPreview && (
            <FlexDynamic
              direction="column"
              wrap="nowrap"
              gap="4"
              w="full"
              justifyContent="flex-end"
              smProps={{ direction: 'row', w: 'auto' }}
            >
              <AddMediaDropdown
                openMediaBin={openMediaBin}
                openMediaUploader={openMediaUploader}
                toggleLabel={dropdownToggleLabel}
                uploadNewAllowed={uploadNewAllowed}
                data-qa-id={`${qaId}-${mediaType.toLowerCase()}-add-media`}
              />
              {mediaList?.length > 1 && isRearrangeMedia && (
                <PrimaryButton
                  variant="secondary"
                  data-qa-id={`${qaId}-${mediaType.toLowerCase()}-rearrange`}
                  onClick={() => openRearrangeModal()}
                  isSmall
                  label={rearrangeButtonText}
                />
              )}
            </FlexDynamic>
          )}
        </FlexDynamic>
        <MediaList
          data-qa-id={qaId}
          hideMedia={(mediaId) => removeProfileMedia({ mediaId })}
          isPreview={isPreview}
          mediaList={mediaList}
          mediaType={mediaType}
          openEditPhotoModal={openPhotoEditor}
          openMediaViewer={openMediaViewer}
          setMediaAsPrimary={setMediaAsPrimary}
        />
      </div>
    );
  }

  // empty media list when isPreview=false
  // user can add media to the profile here
  return (
    <div data-testid={`profile-media-section-${mediaTypeKey}`}>
      <MediaTitle data-qa-id={`${qaId}-${mediaTypeKey}-heading`}>{getMediaText(mediaType, 'pluralTitle')}</MediaTitle>
      <MediaContainer isPreview={false}>
        <AddMediaDropdown
          openMediaBin={openMediaBin}
          openMediaUploader={openMediaUploader}
          toggleLabel={dropdownToggleLabel}
          uploadNewAllowed={uploadNewAllowed}
          data-qa-id={`${qaId}-${mediaType.toLowerCase()}`}
        />
      </MediaContainer>
    </div>
  );
};
